import type { PatientListView } from '../patientList/atoms';

import {
  AGREE_TERMS_SUCCESS,
  CLEAR_PRACTICE,
  FETCH_ORGANIZATION_SUCCESS,
  FETCH_PRACTICE_SUCCESS,
  GET_USER_SUCCESS,
  LOGIN_SUCCESS,
  LOGOUT_SUCCESS,
  UPDATE_ASSIST_SETTINGS,
  UPDATE_PRACTICE_RBL_THRESHOLD,
  UPDATE_PRIMARY_PRACTICE,
  UPDATE_USER_SUCCESS,
  UPDATE_VIDEA_BEACON_CONNECTION_STATUS,
} from './login.constants';

// Types for user organizations
export interface UserOrganization {
  organizationId: string;
  practiceId?: string;
  practiceName?: string;
}

// Types for practice details
export interface Practice {
  id?: string;
  name?: string;
  flags?: string[];
  trialDetails?: {
    isActiveDDAITrial?: boolean;
    isTrialThumbnailShown?: boolean;
    trialStartedOn?: string;
    trialEndsOn?: string;
  };
  rblSettings?: {
    templateName?: string;
  };
}

// Types for user state
export interface User {
  _id?: string;
  id?: string;
  primaryOrganizationId?: string | null;
  primaryPracticeId?: string | null;
  primaryOrganizationName?: string;
  primaryPracticeName?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  userRole?: string;
  organizationFeatureFlags?: string[];
  practiceFeatureFlags?: string[];
  organizations?: UserOrganization[];
  privacyOn?: boolean;
  patientListView?: PatientListView;
  vaultPracticeId?: string;
  vaultPracticeVideaId?: string;
  acceptedTerms?: boolean;
  acceptedTermsVersion?: string;
  organization?: Record<string, unknown>;
  practice?: Practice;
  userSettings?: {
    privacyOn?: boolean;
    patientListView?: PatientListView;
  };
}

// Types for beacon state
export interface BeaconState {
  status?: string;
  errorMessage?: string;
}

// Main login state
export interface LoginState {
  user: User;
  beacon: BeaconState;
}

// Action types
interface BaseAction {
  type: string;
}

interface AgreeTermsSuccessAction extends BaseAction {
  type: typeof AGREE_TERMS_SUCCESS;
  data: { acceptedTermsVersion: string };
}

interface FetchOrganizationSuccessAction extends BaseAction {
  type: typeof FETCH_ORGANIZATION_SUCCESS;
  response: { organization: { data: Record<string, unknown>[] } };
}

interface FetchPracticeSuccessAction extends BaseAction {
  type: typeof FETCH_PRACTICE_SUCCESS;
  response: { practice: Practice };
}

interface GetUserSuccessAction extends BaseAction {
  type: typeof GET_USER_SUCCESS;
  response: { user: User };
}

interface LoginSuccessAction extends BaseAction {
  type: typeof LOGIN_SUCCESS;
  response: { user: User };
}

interface LogoutSuccessAction extends BaseAction {
  type: typeof LOGOUT_SUCCESS;
}

interface UpdateUserSuccessAction extends BaseAction {
  type: typeof UPDATE_USER_SUCCESS;
  user: Partial<User>;
}

interface UpdateVideaBeaconConnectionStatusAction extends BaseAction {
  type: typeof UPDATE_VIDEA_BEACON_CONNECTION_STATUS;
  data: { newStatus: string; errorMessage?: string };
}

export interface UpdatePrimaryPracticeAction extends BaseAction {
  type: typeof UPDATE_PRIMARY_PRACTICE;
  payload: { practiceId: string; practiceName?: string };
}

interface UpdatePracticeRBLThresholdAction extends BaseAction {
  type: typeof UPDATE_PRACTICE_RBL_THRESHOLD;
  payload: string;
}

interface UpdateAssistSettingsAction extends BaseAction {
  type: typeof UPDATE_ASSIST_SETTINGS;
  data: { privacyOn?: boolean; patientListView?: PatientListView };
}

interface ClearPracticeAction extends BaseAction {
  type: typeof CLEAR_PRACTICE;
}

type LoginAction =
  | AgreeTermsSuccessAction
  | FetchOrganizationSuccessAction
  | FetchPracticeSuccessAction
  | GetUserSuccessAction
  | LoginSuccessAction
  | LogoutSuccessAction
  | UpdateUserSuccessAction
  | UpdateVideaBeaconConnectionStatusAction
  | UpdatePrimaryPracticeAction
  | UpdatePracticeRBLThresholdAction
  | UpdateAssistSettingsAction
  | ClearPracticeAction;

const DEFAULT_STATE: LoginState = {
  user: {},
  beacon: {},
};

type ActionHandler = (state: LoginState, action: LoginAction) => LoginState;

const actionsMap: Record<string, ActionHandler> = {
  [AGREE_TERMS_SUCCESS]: handleAgreeTermsSuccess,
  [FETCH_ORGANIZATION_SUCCESS]: handleGetOrganizationSuccess,
  [FETCH_PRACTICE_SUCCESS]: handleGetPracticeSuccess,
  [GET_USER_SUCCESS]: handleGetUserSuccess,
  [LOGIN_SUCCESS]: handleLoginSuccess,
  [LOGOUT_SUCCESS]: handleLogoutSuccess,
  [UPDATE_USER_SUCCESS]: handleUserUpdateSuccess,
  [UPDATE_VIDEA_BEACON_CONNECTION_STATUS]:
    handleUpdateVideaBeaconConnectionStatus,
  [UPDATE_PRIMARY_PRACTICE]: handleUpdatePrimaryPractice,
  [UPDATE_PRACTICE_RBL_THRESHOLD]: handleUpdatePracticeRBLThreshold,
  [UPDATE_ASSIST_SETTINGS]: updateAssistSettings,
  [CLEAR_PRACTICE]: clearPractice,
};

export default function Login(
  state: LoginState = DEFAULT_STATE,
  action: LoginAction | { type: string } = { type: '' },
): LoginState {
  const { type } = action;
  if (actionsMap[type]) {
    return actionsMap[type](state, action as LoginAction);
  }
  return state;
}

function handleAgreeTermsSuccess(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { data } = action as AgreeTermsSuccessAction;
  const { acceptedTermsVersion } = data;

  return {
    ...state,
    user: {
      ...state.user,
      acceptedTerms: true,
      acceptedTermsVersion,
    },
  };
}

function handleGetOrganizationSuccess(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { response } = action as FetchOrganizationSuccessAction;
  const { organization: responseData } = response;
  const { data: organizationList } = responseData;

  return {
    ...state,
    user: {
      ...state.user,
      organization: organizationList[0],
    },
  };
}

function handleGetPracticeSuccess(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { response } = action as FetchPracticeSuccessAction;
  const { practice } = response;

  return {
    ...state,
    user: {
      ...state.user,
      practiceFeatureFlags: practice?.flags,
      practice,
    },
  };
}

function handleGetUserSuccess(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { response } = action as GetUserSuccessAction;
  const { user: userResponse } = response;
  // Preserve existing primaryPracticeId if already set - the backend always sends
  // the first practice which may not be the one the user was previously viewing
  const existingPrimaryPracticeId = state.user?.primaryPracticeId;
  const existingPrimaryPracticeName = state.user?.primaryPracticeName;
  return {
    ...state,
    user: {
      ...userResponse,
      ...(existingPrimaryPracticeId &&
        existingPrimaryPracticeName && {
          primaryPracticeId: existingPrimaryPracticeId,
          primaryPracticeName: existingPrimaryPracticeName,
        }),
    },
  };
}

function handleLoginSuccess(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { response } = action as LoginSuccessAction;
  const { user } = response;
  return {
    ...state,
    user: { ...user },
  };
}

function handleLogoutSuccess(state: LoginState): LoginState {
  return {
    ...state,
    user: {},
  };
}

function handleUserUpdateSuccess(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { user } = action as UpdateUserSuccessAction;
  return {
    ...state,
    user: {
      ...state.user,
      ...user,
    },
  };
}

function handleUpdateVideaBeaconConnectionStatus(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { data } = action as UpdateVideaBeaconConnectionStatusAction;
  const { newStatus, errorMessage } = data;

  return {
    ...state,
    beacon: {
      ...state.beacon,
      status: newStatus,
      errorMessage,
    },
  };
}

function handleUpdatePrimaryPractice(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { payload } = action as UpdatePrimaryPracticeAction;
  const { practiceId, practiceName } = payload;

  return {
    ...state,
    user: {
      ...state.user,
      primaryPracticeId: practiceId,
      primaryPracticeName: practiceName,
    },
  };
}

function handleUpdatePracticeRBLThreshold(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { payload } = action as UpdatePracticeRBLThresholdAction;
  return {
    ...state,
    user: {
      ...state.user,
      practice: {
        ...state.user.practice,
        rblSettings: {
          ...state.user.practice?.rblSettings,
          templateName: payload,
        },
      },
    },
  };
}

export function updateAssistSettings(
  state: LoginState,
  action: LoginAction,
): LoginState {
  const { data } = action as UpdateAssistSettingsAction;
  const { privacyOn, patientListView } = data;
  return {
    ...state,
    user: {
      ...state.user,
      // If defined, update w/ the new values
      ...(typeof privacyOn !== 'undefined' && { privacyOn }),
      ...(!!patientListView && { patientListView }),
    },
  };
}

// For director users, we need to ensure we clear practice state when navigating to the switchboard so we don't see any lingering state
function clearPractice(state: LoginState): LoginState {
  return {
    ...state,
    user: {
      ...state.user,
      primaryPracticeId: undefined,
      primaryPracticeName: undefined,
      practiceFeatureFlags: [],
      practice: undefined,
    },
  };
}
